import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import type { UUID } from "@/Shared/types";
import type { BookShipmentResponse } from "@freightsimple/generated-dashboard-openapi-client/models/BookShipmentResponse";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { Document } from "@freightsimple/generated-dashboard-openapi-client/models/Document";
import type { DocumentType } from "@freightsimple/generated-dashboard-openapi-client/models/DocumentType";
import type { DownloadThumbnailResponse } from "@freightsimple/generated-dashboard-openapi-client/models/DownloadThumbnailResponse";
import type { EquipmentType } from "@freightsimple/generated-dashboard-openapi-client/models/EquipmentType";
import type { GetOneShipmentResponse } from "@freightsimple/generated-dashboard-openapi-client/models/GetOneShipmentResponse";
import type { GetShipmentsResponse } from "@freightsimple/generated-dashboard-openapi-client/models/GetShipmentsResponse";
import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { PdfResponse } from "@freightsimple/generated-dashboard-openapi-client/models/PdfResponse";
import type { PollQuotesResponse } from "@freightsimple/generated-dashboard-openapi-client/models/PollQuotesResponse";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SharedDedicated } from "@freightsimple/generated-dashboard-openapi-client/models/SharedDedicated";
import { downloadBase64File } from "@/Helpers/downloadBase64File";
import { fileNameForDocumentType } from "@/Helpers/fileNameForDocumentType";
import moment from "moment";

interface ShipmentService {
  getShipment: (shipmentId: UUID) => Promise<GetOneShipmentResponse>;
  getPreBookingShipment: (shipmentId: UUID) => Promise<PreBookingShipment>;
  getShipments: () => Promise<GetShipmentsResponse>;
  pollForQuotes: (shipmentId: UUID) => Promise<PollQuotesResponse>;
  selectQuote: (shipmentId: UUID, quoteId: UUID) => Promise<void>;
  bookShipment: (shipmentId: UUID) => Promise<BookShipmentResponse>;
  markReviewingPriorToBookingShipment: (shipmentId: UUID) => Promise<void>;
  downloadPickupPackage: (shipmentId: UUID) => Promise<void>;
  downloadDocument: (
    documentId: UUID,
    documentType: DocumentType,
  ) => Promise<void>;
  downloadInvoice: (
    shipmentId: UUID,
    invoiceIdentifier: string,
    key: string,
  ) => Promise<void>;
  getDocuments: (shipmentId: UUID) => Promise<Document[]>;
  getInvoices: (shipmentId: UUID) => Promise<Invoice[]>;
  deleteQuotedShipment: (shipmentId: UUID, reason: string) => Promise<void>;
  hasBookedOnlyOneShipment: () => Promise<boolean>;
  setBroker: (shipmentId: UUID, savedBrokerId: UUID) => Promise<void>;
  downloadInvoicesZip: (
    shipmentId: UUID,
    invoices: { invoicePdfKey: string; invoiceIdentifier: string }[],
    zipFileName?: string,
  ) => Promise<void>;
  downloadThumbnail: (documentId: UUID) => Promise<DownloadThumbnailResponse>;
  getDocumentPdf: (documentId: UUID) => Promise<PdfResponse>;
  updateContact: (
    shipmentId: UUID,
    locationContext: LocationContext,
    contact: Contact,
  ) => Promise<void>;
  updateLocation: (
    shipmentId: UUID,
    locationContext: LocationContext,
    location: Location,
  ) => Promise<void>;
  updateReferenceAndNotes: (
    shipmentId: UUID,
    locationContext: LocationContext,
    reference: string,
    amazonReferenceId: string | undefined,
    amazonShipmentId: string | undefined,
    boothNumber: string,
    notes: string,
  ) => Promise<void>;
  requestManualQuotes: (
    shipmentId: UUID,
    equipmentType: EquipmentType,
    sharedDedicated: SharedDedicated,
    tarpRequired: boolean | undefined,
    linearFeet: number | undefined,
    notes: string,
  ) => Promise<void>;
  downloadQuote: (quoteId: UUID) => Promise<void>;
}

export function useShipmentService(): ShipmentService {
  const shipmentApi = useShipmentsApi();

  return {
    getShipment: async function (
      shipmentId: UUID,
    ): Promise<GetOneShipmentResponse> {
      const response = await shipmentApi.getOneShipment({
        shipmentId,
      });

      return response;
    },

    getPreBookingShipment: async function (
      shipmentId: UUID,
    ): Promise<PreBookingShipment> {
      const response = await shipmentApi.getPreBookingShipment({
        shipmentId,
      });

      return response;
    },

    getShipments: async function (): Promise<GetShipmentsResponse> {
      const response = await shipmentApi.getShipments();

      // Sort by pickup date
      response.booked.sort(function (a, b) {
        return moment(a.pickupDate).unix() - moment(b.pickupDate).unix();
      });

      response.quoted.sort(function (a, b) {
        return moment(a.pickupDate).unix() - moment(b.pickupDate).unix();
      });

      return response;
    },

    pollForQuotes: async function (
      shipmentId: UUID,
    ): Promise<PollQuotesResponse> {
      const response = await shipmentApi.pollQuotes({
        shipmentId,
      });

      return response;
    },

    selectQuote: async function (shipmentId: UUID, quoteId: UUID) {
      await shipmentApi.selectQuote({
        selectQuoteRequest: {
          shipmentId,
          quoteId,
        },
      });
    },

    bookShipment: async function (shipmentId: UUID) {
      return await shipmentApi.bookShipment({
        bookShipmentRequest: {
          shipmentId,
        },
      });
    },

    markReviewingPriorToBookingShipment: async function (shipmentId: UUID) {
      await shipmentApi.markReviewingPriorToBookingShipment({
        shipmentId,
      });
    },

    downloadPickupPackage: async function (shipmentId: UUID) {
      const billOfLading = await shipmentApi.getPickupPackagePdf({
        shipmentId,
      });

      downloadBase64File(
        billOfLading.base64EncodedPdf,
        "pickupPackage.pdf",
        "application/pdf",
      );
    },

    downloadDocument: async function (
      documentId: UUID,
      documentType: DocumentType,
    ) {
      const response = await shipmentApi.getDocumentPdf({
        documentId,
      });

      downloadBase64File(
        response.base64EncodedPdf,
        fileNameForDocumentType(documentType),
        "application/pdf",
      );
    },

    downloadInvoice: async function (
      shipmentId: UUID,
      invoiceIdentifier: string,
      key: string,
    ) {
      const response = await shipmentApi.getInvoicePdf({
        shipmentId,
        key,
      });

      downloadBase64File(
        response.base64EncodedPdf,
        `invoice-${invoiceIdentifier}.pdf`,
        "application/pdf",
      );
    },

    getDocuments: async function (shipmentId: UUID) {
      const response = await shipmentApi.listDocuments({
        shipmentId,
      });

      return response.documents;
    },

    getInvoices: async function (shipmentId: UUID) {
      return await shipmentApi.listShipmentInvoices({
        shipmentId,
      });
    },

    deleteQuotedShipment: async function (shipmentId: UUID, reason: string) {
      try {
        await shipmentApi.deleteQuotedShipment({
          shipmentId,
          reason,
        });
      } catch (e) {
        console.warn("deleteQuotedShipment failed", e);
      }
    },

    hasBookedOnlyOneShipment: async function (): Promise<boolean> {
      const response = await shipmentApi.hasBookedOnlyOneShipment();

      return response.hasBookedOnlyOneShipment;
    },

    setBroker: async function (shipmentId: UUID, savedBrokerId: UUID) {
      await shipmentApi.setBroker({
        shipmentId,
        savedBrokerId,
      });
    },

    updateContact: async function (
      shipmentId: UUID,
      locationContext: LocationContext,
      contact: Contact,
    ) {
      await shipmentApi.setContact({
        setContactRequest: {
          shipmentId,
          locationContext,
          contact,
        },
      });
    },

    updateLocation: async function (
      shipmentId: UUID,
      locationContext: LocationContext,
      location: Location,
    ) {
      await shipmentApi.setAddress({
        setAddressRequest: {
          shipmentId,
          locationContext,
          businessName: location.businessName,
          address: location.address,
        },
      });
    },

    updateReferenceAndNotes: async function (
      shipmentId: UUID,
      locationContext: LocationContext,
      reference: string,
      amazonReferenceId: string | undefined,
      amazonShipmentId: string | undefined,
      boothNumber: string,
      notes: string,
    ) {
      // The ids are only handed in for an Amazon warehouse delivery; the
      // server packs them, so the packed string is not sent alongside them.
      const referenceFields =
        amazonReferenceId !== undefined || amazonShipmentId !== undefined
          ? {
              amazonReferenceId: amazonReferenceId ?? "",
              amazonShipmentId: amazonShipmentId ?? "",
            }
          : { reference };
      await shipmentApi.setReferenceAndNotes({
        setReferenceAndNotesRequest: {
          shipmentId,
          locationContext,
          ...referenceFields,
          boothNumber,
          notes,
        },
      });
    },

    requestManualQuotes: async function (
      shipmentId: UUID,
      equipmentType: EquipmentType,
      sharedDedicated: SharedDedicated,
      tarpRequired: boolean | undefined,
      linearFeet: number | undefined,
      notes: string,
    ) {
      await shipmentApi.requestManualQuotes({
        requestManualQuotesRequest: {
          shipmentId,
          equipmentType,
          sharedDedicated,
          ...(tarpRequired !== undefined ? { tarpRequired } : {}),
          ...(linearFeet !== undefined ? { linearFeet } : {}),
          notes,
        },
      });
    },

    downloadInvoicesZip: async function (
      shipmentId: UUID,
      invoices: { invoicePdfKey: string; invoiceIdentifier: string }[],
      zipFileName: string = "invoices.zip",
    ) {
      const response = await shipmentApi.getInvoicesZip({
        getInvoicesZipRequest: {
          shipmentId,
          invoices,
        },
      });

      downloadBase64File(
        response.base64EncodedZip,
        zipFileName,
        "application/zip",
      );
    },

    downloadThumbnail: async function (
      documentId: UUID,
    ): Promise<DownloadThumbnailResponse> {
      const response = await shipmentApi.downloadThumbnail({
        documentId,
      });

      return response;
    },

    getDocumentPdf: async function (documentId: UUID): Promise<PdfResponse> {
      const response = await shipmentApi.getDocumentPdf({
        documentId,
      });

      return response;
    },

    downloadQuote: async function (quoteId: UUID) {
      const response = await shipmentApi.getQuotePdf({
        quoteId,
      });

      downloadBase64File(
        response.base64EncodedPdf,
        `quote-${quoteId}.pdf`,
        "application/pdf",
      );
    },
  };
}
